import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthProvider';
import { BareLayout } from '@/components/layout/BareLayout';
import { Button } from '@/components/ui/button';
import { callRpc } from '@/data/rpc';
import { TermsGateScreen } from './TermsGateScreen';
import { legalRow, type LegalStatus } from './legalStatus';

/**
 * The Terms gate (change spec terms-acceptance, design 3). Sits between RequireAuth and AgencyProvider:
 * after sign-in it reads `my_legal_status()` and renders the portal only when the signed-in user has
 * accepted the Terms and Privacy versions in force. Nothing agency-scoped (agency, team, realtime) is
 * mounted before that.
 *
 * It fails CLOSED: a failed or malformed read shows a retry, never the portal (unlike the suspended
 * check, which falls back to the portal). The server keeps the versions: nothing is hardcoded here.
 */

export interface LegalContextValue {
  termsVersion: string | null;
  privacyVersion: string | null;
  /** The first acceptance of any Terms version (the start of the opt-out window). */
  firstTermsAcceptedAt: string | null;
  reload: () => void;
}

const LegalContext = React.createContext<LegalContextValue | null>(null);

export function useLegalStatus(): LegalContextValue {
  const ctx = React.useContext(LegalContext);
  if (!ctx) throw new Error('useLegalStatus must be used inside LegalProvider');
  return ctx;
}

type State = { status: 'loading' } | { status: 'error' } | { status: 'ready'; row: LegalStatus };

export function LegalProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { client, session } = useAuth();
  const userId = session?.user.id ?? null;
  const [state, setState] = React.useState<State>({ status: 'loading' });
  const [nonce, setNonce] = React.useState(0);
  const reload = React.useCallback(() => setNonce((n) => n + 1), []);

  React.useEffect(() => {
    if (!userId) return;
    let active = true;
    // A reload from the gate (a stale version) keeps the screen, and its Toaster, mounted meanwhile.
    setState((s) => (s.status === 'ready' ? s : { status: 'loading' }));
    void callRpc<unknown>(client, 'my_legal_status', {}).then(
      (res) => {
        if (!active) return;
        const row = res.error ? null : legalRow(res.data);
        if (!row) {
          console.warn('LegalProvider: my_legal_status failed', res.error);
          setState({ status: 'error' });
          return;
        }
        setState({ status: 'ready', row });
      },
      (err: unknown) => {
        if (!active) return;
        console.warn('LegalProvider: my_legal_status threw', err);
        setState({ status: 'error' });
      },
    );
    return () => {
      active = false;
    };
  }, [client, userId, nonce]);

  const row = state.status === 'ready' ? state.row : null;
  const value = React.useMemo<LegalContextValue>(
    () => ({
      termsVersion: row?.terms_version ?? null,
      privacyVersion: row?.privacy_version ?? null,
      firstTermsAcceptedAt: row?.first_terms_accepted_at ?? null,
      reload,
    }),
    [row, reload],
  );

  if (state.status === 'loading') return <Centered>{t('legal.checking')}</Centered>;
  if (state.status === 'error') {
    return (
      // Closed but not a trap: the bare chrome keeps sign-out and the language switch reachable.
      <BareLayout>
        <div role="alert" className="flex flex-col items-center gap-3 pt-12 text-center text-base text-danger">
          <p>{t('legal.error')}</p>
          <Button type="button" variant="outline" size="sm" onClick={reload}>
            {t('legal.retry')}
          </Button>
        </div>
      </BareLayout>
    );
  }
  if (state.row.needs_acceptance) {
    return (
      // The chrome stays mounted across a version change, so the CWT01 toast survives the new screen.
      <BareLayout>
        <TermsGateScreen
          // A new version is a new screen: the tick never carries over to a text the user has not seen.
          key={`${state.row.terms_version}|${state.row.privacy_version}`}
          status={state.row}
          onAccepted={(next) => setState({ status: 'ready', row: next })}
          onStale={reload}
        />
      </BareLayout>
    );
  }
  return <LegalContext.Provider value={value}>{children}</LegalContext.Provider>;
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div role="status" aria-live="polite" className="grid min-h-screen place-items-center bg-canvas p-6 text-base text-ink-muted">
      {children}
    </div>
  );
}
